import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import {
  addUserAddress,
  updateUserAddress,
} from "./../../redux/actions/action-creator";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./locationPopup.css";

const initialState = {
  id: "",
  address: "",
  address_title: "",
  building: "",
  floor: "",
  house_name: "",
  landmark: "",
  latitude: "",
  longitude: "",
  user_name: "",
  category: "",
};

const categoryOptions = [
  { value: "1", title: "Home", icon: "ph ph-house" },
  { value: "2", title: "Work", icon: "ph ph-briefcase" },
  { value: "3", title: "Other", icon: "ph ph-map-pin" },
];

const formRows = [
  [
    {
      name: "building",
      placeholder: "Office Name / Number",
      required: true,
    },
    { name: "floor", placeholder: "Floor" },
  ],
  [
    { name: "house_name", placeholder: "Tower / Wing" },
    { name: "user_name", placeholder: "Enter Full Name", required: true },
  ],
];

const optionalFields = [
  {
    name: "landmark",
    placeholder: "How to reach (Optional)",
  },
];

const payloadFields = [
  "address",
  "address_title",
  "building",
  "floor",
  "house_name",
  "landmark",
  "latitude",
  "longitude",
  "user_name",
  "category",
];

const getText = (value) =>
  value === null || value === undefined ? "" : String(value);

const getAddressId = (address) =>
  getText(address?.id || address?.address_id || address?.user_address_id);

const getCategoryTitle = (category) =>
  categoryOptions.find((item) => item.value === getText(category))?.title || "";

const getCategoryValue = (address) => {
  const category = getText(address?.category);
  const matchedValue = categoryOptions.find(
    (item) =>
      item.value === category || item.title.toLowerCase() === category.toLowerCase()
  );

  if (matchedValue) return matchedValue.value;
  if (category) return category;

  const title = getText(address?.address_title).toLowerCase();
  const matchedCategory = categoryOptions.find(
    (item) => item.title.toLowerCase() === title
  );

  return matchedCategory?.value || "";
};

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || error?.message_text || fallback;

const normalizeSuggestion = (suggestion) => {
  const prediction = suggestion?.placePrediction || suggestion || {};
  const description =
    prediction?.text?.text ||
    prediction?.description ||
    prediction?.structured_formatting?.main_text ||
    "";
  const placeId = prediction?.placeId || prediction?.place_id || prediction?.id || "";

  return description && placeId
    ? { place_id: placeId, description }
    : null;
};

const LocationPopup = ({ open, onClose, onSelect, editData, onSaved }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [state, setState] = useState(initialState);
  const [search, setSearch] = useState("");
  const [currentAddress, setCurrentAddress] = useState("");
  const [suggestions, setSuggestions] = useState([]);

  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const geocoder = useRef(null);

  const selectedLocation = useRef(null);
  const isEditMode = Boolean(editData);

  const setAddressLocation = (location, formattedAddress) => {
    selectedLocation.current = location;

    setState((prevState) => ({
      ...prevState,
      address: formattedAddress || prevState.address,
      latitude: getText(location?.lat),
      longitude: getText(location?.lng),
    }));

    if (formattedAddress) {
      setSearch(formattedAddress);
      setCurrentAddress(formattedAddress);
    }
  };

  const reverseGeocode = (location) => {
    if (!geocoder.current) return;

    geocoder.current.geocode({ location }, (results, status) => {
      if (status === "OK" && results[0]) {
        setAddressLocation(location, results[0].formatted_address);
      }
    });
  };

  const getEditLocation = () => {
    const lat = Number(editData?.latitude ?? editData?.lat);
    const lng = Number(editData?.longitude ?? editData?.lng);

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

    return { lat, lng };
  };

  const initializeMap = (location, shouldReverseGeocode = true) => {
    selectedLocation.current = location;
    setState((prevState) => ({
      ...prevState,
      latitude: getText(location.lat),
      longitude: getText(location.lng),
    }));

    mapInstance.current = new window.google.maps.Map(mapRef.current, {
      center: location,
      zoom: 16,
      disableDefaultUI: true,
    });

    if (shouldReverseGeocode) {
      reverseGeocode(location);
    }

    mapInstance.current.addListener("idle", () => {
      const center = mapInstance.current.getCenter();
      const lat = center.lat();
      const lng = center.lng();

      reverseGeocode({ lat, lng });
    });
  };

  useEffect(() => {
    if (!open) return;
    selectedLocation.current = null;
    setSuggestions([]);

    if (editData) {
      const category = getCategoryValue(editData);
      const addressTitle = getText(editData.address_title) || getCategoryTitle(category);

      setState({
        id: getAddressId(editData),
        address: getText(editData.address),
        address_title: addressTitle,
        building: getText(editData.building),
        floor: getText(editData.floor),
        house_name: getText(editData.house_name),
        landmark: getText(editData.landmark),
        latitude: getText(editData.latitude ?? editData.lat),
        longitude: getText(editData.longitude ?? editData.lng),
        user_name: getText(editData.user_name),
        category,
      });

      setSearch(getText(editData.address));
      setCurrentAddress(getText(editData.address));
      return;
    }

    setState(initialState);
    setSearch("");
    setCurrentAddress("");
  }, [editData, open]);

  useEffect(() => {
    if (!open || !window.google || !mapRef.current) return;

    geocoder.current = new window.google.maps.Geocoder();

    const editLocation = getEditLocation();

    if (editLocation) {
      initializeMap(editLocation, !editData?.address);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        initializeMap({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
      },
      () => {
        toast.warn("Location permission denied", {
          position: "top-center",
        });
      }
    );
  }, [open, editData]);

  useEffect(() => {
    let cancelled = false;

    if (!open || search.length <= 2) {
      setSuggestions([]);
      return undefined;
    }

    const loadSuggestions = async () => {
      const places = window.google?.maps?.places;
      const fetchSuggestions = places?.AutocompleteSuggestion?.fetchAutocompleteSuggestions;

      if (fetchSuggestions) {
        try {
          const result = await fetchSuggestions({ input: search });
          const nextSuggestions = (result?.suggestions || [])
            .map(normalizeSuggestion)
            .filter(Boolean)
            .slice(0, 5);

          if (!cancelled) setSuggestions(nextSuggestions);
          return;
        } catch (error) {
          if (!cancelled) setSuggestions([]);
        }
      }

      if (!cancelled) setSuggestions([]);
    };

    loadSuggestions();

    return () => {
      cancelled = true;
    };
  }, [open, search]);

  const handleFieldChange = (event) => {
    const { name, value } = event.target;

    setState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSearchChange = (event) => {
    const { value } = event.target;

    setSearch(value);
    setCurrentAddress(value);
    setState((prevState) => ({
      ...prevState,
      address: value,
    }));
  };

  const handleSelectSuggestion = (place) => {
    const fallbackAddress = place.description;

    setSearch(fallbackAddress);
    setCurrentAddress(fallbackAddress);
    setSuggestions([]);
    setState((prevState) => ({
      ...prevState,
      address: fallbackAddress,
    }));

    geocoder.current?.geocode({ placeId: place.place_id }, (results, status) => {
      if (status === "OK" && results[0]) {
        const loc = results[0].geometry.location;
        const location = { lat: loc.lat(), lng: loc.lng() };
        const formattedAddress = results[0].formatted_address || fallbackAddress;

        setAddressLocation(location, formattedAddress);
        mapInstance.current?.panTo(location);
      }
    });
  };

  const handleCategorySelect = (option) => {
    setState((prevState) => ({
      ...prevState,
      category: option.value,
      address_title: option.title,
    }));
  };

  const handleCurrentLocation = () => {
    if (!mapInstance.current) return;

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const location = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };

        mapInstance.current.panTo(location);
        mapInstance.current.setZoom(17);
        reverseGeocode(location);
      },
      () => {
        toast.warn("Unable to fetch location", {
          position: "top-center",
        });
      }
    );
  };

  const getPayload = () => {
    const location = selectedLocation.current || {};
    const categoryTitle = getCategoryTitle(state.category);

    return {
      ...state,
      address: getText(state.address || currentAddress || search).trim(),
      address_title: getText(state.address_title || categoryTitle).trim(),
      latitude: getText(location.lat ?? state.latitude).trim(),
      longitude: getText(location.lng ?? state.longitude).trim(),
      building: getText(state.building).trim(),
      floor: getText(state.floor).trim(),
      house_name: getText(state.house_name).trim(),
      landmark: getText(state.landmark).trim(),
      user_name: getText(state.user_name).trim(),
      category: getText(state.category).trim(),
    };
  };

  const createFormData = (payload) => {
    const data = new FormData();

    payloadFields.forEach((field) => {
      data.append(field, payload[field] ?? "");
    });

    if (isEditMode) {
      const addressId = getAddressId(editData) || payload.id;

      data.append("id", addressId);
    }

    return data;
  };

  const validatePayload = (payload) => {
    if (!payload.address) return "Fill Address ...!";
    if (!payload.address_title && !payload.category) return "Select Save As ...!";
    if (!payload.building) return "Fill Office / Building Name ...!";
    if (!payload.user_name) return "Fill Full Name ...!";
    if (isEditMode && !getAddressId(editData) && !payload.id) {
      return "Address id missing for update.";
    }

    return "";
  };

  const handleConfirm = async (event) => {
    event.preventDefault();
    if (loading) return;

    const payload = getPayload();
    const validationMessage = validatePayload(payload);

    if (validationMessage) {
      toast.warn(validationMessage, {
        position: "top-center",
      });
      return;
    }

    setLoading(true);

    try {
      const formData = createFormData(payload);
      const action = isEditMode ? updateUserAddress : addUserAddress;
      const response = await dispatch(action(formData));

      toast.success(
        response?.message ||
          (isEditMode ? "Address updated successfully!" : "Address saved successfully!"),
        {
          position: "top-center",
        }
      );

      onSelect?.(payload);
      onSaved?.(response, payload);
      setState(initialState);
      setSearch("");
      setCurrentAddress("");
      onClose?.();
    } catch (error) {
      toast.error(
        getErrorMessage(
          error,
          isEditMode ? "Address update failed!" : "Address save failed!"
        ),
        {
          position: "top-center",
        }
      );
    } finally {
      setLoading(false);
    }
  };


  if (!open) return null;

  return (
    <div className="location-overlay">
      <div className="location-modal">

        <div className="location-header">
          <div className="heading-title">
            {isEditMode ? "Edit Address" : "Add New Address"}
          </div>
          <button type="button" className="close-btn-location" onClick={onClose} aria-label="Close location popup">
            ×
          </button>
        </div>

        <div className="location-search-div">
          <div className="location-search">
            <i className="ph ph-map-pin"></i>
            <input
              aria-label="Search location"
              type="text"
              placeholder="Search location"
              value={search}
              onChange={handleSearchChange}
            />
            <i className="ph ph-magnifying-glass"></i>
          </div>

          {suggestions.length > 0 && (
            <div className="suggestions-box">
              {suggestions.map((item) => (
                <div
                  key={item.place_id || item.description}
                  className="suggestion-item"
                  onClick={() => handleSelectSuggestion(item)}
                >
                  {item.description}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="map-wrapper">

          <div ref={mapRef} className="map-container" />

          <div className="center-pin">
            <div className="pin"></div>
            <div className="pin-shadow"></div>
          </div>
          <button
            type="button"
            className="current-location-btn"
            onClick={handleCurrentLocation}
            aria-label="Use current location"
          >
            <i className="ph ph-crosshair" aria-hidden="true"></i>
          </button>
        </div>

        <form className="address-form" onSubmit={handleConfirm}>
          {formRows.map((row) => (
            <div className="form-row" key={row.map((field) => field.name).join("-")}>
              {row.map((field) => (
                <input
                  key={field.name}
                  name={field.name}
                  placeholder={field.placeholder}
                  aria-label={field.placeholder}
                  value={state[field.name]}
                  onChange={handleFieldChange}
                  required={field.required}
                />
              ))}
            </div>
          ))}

          {optionalFields.map((field) => (
            <input
              key={field.name}
              name={field.name}
              className="full-input"
              placeholder={field.placeholder}
              aria-label={field.placeholder}
              value={state[field.name]}
              onChange={handleFieldChange}
            />
          ))}

          <div className="save-as">
            <label>Save As:</label>

            <div className="save-buttons">
              {categoryOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={state.category === option.value ? "active" : ""}
                  onClick={() => handleCategorySelect(option)}
                >
                  <i className={option.icon} aria-hidden="true"></i>
                  <span>{option.title}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="confirm-btn-div">
            <button className="confirm-btn" type="submit" disabled={loading}>
              {loading
                ? isEditMode
                  ? "Updating..."
                  : "Saving..."
                : isEditMode
                ? "Update Address"
                : "Confirm Location & Proceed"}
            </button>
          </div>
        </form>

      </div>
    </div>
  );
};

export default LocationPopup;
